import React, { useEffect, useState } from "react";

import { useGetPropertyBillingHistory } from "manager/hooks/api";
import { Property } from "manager/interfaces/api";
import { Modal } from "shared/components/Modal";
import PaginationControls from "shared/components/PaginationControls";
import { HTTP_ERROR_CODES } from "shared/config/constants";
import useModal from "shared/hooks/useModal";

import {
  BILLING_HISTORY_PAGE_SIZE,
  billingHistoryDetail,
  billingHistoryEventLabel,
  billingHistoryPage,
  formatBillingDateTime,
} from "./billingDisplay";
import {
  HistoryDetail,
  HistoryItem,
  HistoryList,
  HistoryMeta,
  HistoryPager,
  HistoryReason,
  HistoryScroll,
  HistoryTitle,
} from "./styled";

interface BillingHistoryModalProps {
  property: Property;
}

const BillingHistoryModal = ({ property }: BillingHistoryModalProps) => {
  const { closeActiveModalDialog } = useModal();
  const [page, setPage] = useState(1);
  const { data, error, isError, isLoading } = useGetPropertyBillingHistory({
    propertyId: property.id,
    page,
    pageSize: BILLING_HISTORY_PAGE_SIZE,
  });
  const { rows, total } = billingHistoryPage(
    data,
    page,
    BILLING_HISTORY_PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [property.id]);

  useEffect(() => {
    const statusCode = (error as { statusCode?: number } | undefined)
      ?.statusCode;
    if (statusCode === HTTP_ERROR_CODES.notFound && page > 1) {
      setPage(1);
    }
  }, [error, page]);

  return (
    <Modal
      title="History"
      subtitle={`${property.name} · Who / when / why. Django deactivate is called out.`}
      submit={closeActiveModalDialog}
      submitButtonLabel="Close"
      showSubmitButton
      showCancelLink={false}
      width={560}
    >
      <Modal.Body>
        <HistoryScroll>
          {isLoading && <p>Loading history…</p>}
          {isError && !isLoading && (
            <p role="alert">Couldn’t load this page of history.</p>
          )}
          {!isLoading && !isError && !rows.length && (
            <p>No billing changes yet.</p>
          )}
          {!isError && (
            <HistoryList>
              {rows.map((row) => {
                const detail = billingHistoryDetail(row);
                return (
                  <HistoryItem key={row.id}>
                    <HistoryTitle>
                      {billingHistoryEventLabel(row.billingEvent)}
                    </HistoryTitle>
                    <HistoryMeta>
                      {formatBillingDateTime(row.createdAt)}
                      {row.billingActorUserEmail
                        ? ` · ${row.billingActorUserEmail}`
                        : ""}
                    </HistoryMeta>
                    {detail && <HistoryDetail>{detail}</HistoryDetail>}
                    {row.billingReason && (
                      <HistoryReason>{row.billingReason}</HistoryReason>
                    )}
                  </HistoryItem>
                );
              })}
            </HistoryList>
          )}
        </HistoryScroll>
        <HistoryPager>
          <PaginationControls
            current={page}
            total={total}
            pageSize={BILLING_HISTORY_PAGE_SIZE}
            itemLabel="changes"
            onChange={(nextPage) => {
              if (nextPage) {
                setPage(nextPage);
              }
            }}
          />
        </HistoryPager>
      </Modal.Body>
    </Modal>
  );
};

export default BillingHistoryModal;
